"use strict";

// Pull in common's DragDrop + MultiSelect helpers. esbuild will bundle these
// into server/_public/js/app.js so the kanban can use them via globals.
require('../../common/components/js/drag-drop.js');
require('../../common/components/js/multi-select.js');
// Connects to socket.io and re-emits task events as DOM events.
require('./realtime.js');

// Live Activity card (dashboard). No-ops on pages without #activity-list.
require('./activity-feed.js');
// Workspace thread - history paging + live activity interleaving.
require('./workspace.js');
// Project dashboard — fetches metrics + renders Chart.js charts.
require('./dashboard.js');
// Project chat — single #general channel, image attachments, realtime.
require('./communication.js');
// Tag input (common component): pills + type-to-suggest, on the task panel.
require('../../common/components/js/tag-input.js');
// App shell: sidebar, counts, right panel, AJAX forms.
require('./shell.js');
// Entity-header overflow menu (3-dot trigger at narrow widths).
require('./header-menu.js');
// System Evolution — interactive component/association diagram.
require('./evolution-diagram.js');
// Wishlist — drag-drop update filing onto wishes (curators only).
require('./wishlist.js');
// Project Home: the live thread (General or a DM).
require('./project-home.js');

(function () {
  function initKanban() {
    var board = document.querySelector('.kanban[data-project-id]');
    if (!board) return;

    var orgSlug = board.getAttribute('data-org-slug');
    var projectId = board.getAttribute('data-project-id');
    var csrf = board.getAttribute('data-csrf');
    if (!orgSlug || !projectId || !csrf) return;

    // Multi-select: cmd/ctrl+click to toggle, shift+click for range, plain
    // click to single-select. Drag uses the selection if the dragged card is
    // part of it; otherwise falls back to single-card move.
    var multi = window.MultiSelect({
      container: '.kanban',
      itemSelector: '.kanban-card',
      idAttr: 'data-task-id',
      selectedClass: 'kanban-card--selected'
    });

    var selBar = document.querySelector('.kanban-selection-bar');
    var selBarN = selBar ? selBar.querySelector('.kanban-selection-bar__n') : null;
    var selBarClear = selBar ? selBar.querySelector('.kanban-selection-bar__clear') : null;

    function refreshSelectionUi() {
      var n = multi.selectedIds.size;
      if (!selBar) return;
      if (n > 0) {
        selBar.hidden = false;
        if (selBarN) selBarN.textContent = String(n);
      } else {
        selBar.hidden = true;
      }
    }
    if (selBarClear) {
      selBarClear.addEventListener('click', function () {
        multi.clear();
        refreshSelectionUi();
      });
    }

    // Click on a card → select. Title link + any interactive child
    // (button, form, select, input) are excluded so they fire their own
    // handlers / native behaviour. Without this guard the preventDefault
    // below cancels form submits and swallows button clicks for in-card
    // actions like Retry / Reject / Complete / Activate.
    board.addEventListener('click', function (e) {
      if (e.target.closest('.kanban-card__title-link')) return;
      if (e.target.closest('a, button, form, input, select, textarea')) return;
      var card = e.target.closest('.kanban-card');
      if (!card || !board.contains(card)) return;
      e.preventDefault();
      var id = card.getAttribute('data-task-id');
      multi.select(id, card, e.metaKey || e.ctrlKey, e.shiftKey);
      refreshSelectionUi();
    });

    // Double-click anywhere on the card (not the title link or any
    // interactive child) → open task detail.
    board.addEventListener('dblclick', function (e) {
      if (e.target.closest('.kanban-card__title-link')) return;
      if (e.target.closest('a, button, form, input, select, textarea')) return;
      var card = e.target.closest('.kanban-card');
      if (!card) return;
      var link = card.querySelector('.kanban-card__title-link');
      if (link) window.location.href = link.getAttribute('href');
    });

    // Esc clears the current selection.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && multi.selectedIds.size > 0) {
        multi.clear();
        refreshSelectionUi();
      }
    });

    // Click on empty board background (not a card) clears selection.
    board.addEventListener('mousedown', function (e) {
      if (!e.target.closest('.kanban-card') && multi.selectedIds.size > 0) {
        multi.clear();
        refreshSelectionUi();
      }
    });

    // Make every kanban card draggable, with multi-drag wired up.
    window.DragDrop.initDraggable({
      container: '.kanban',
      itemSelector: '.kanban-card',
      idAttr: 'data-task-id',
      multiSelect: multi
    });

    // Within-column drag indicator: a thin marker line shown between cards
    // at the predicted insertion point. Only rendered when the drag started
    // in the same column the cursor is currently over.
    var dragSourceCol = null;
    var draggedIds = [];
    var dropMarker = document.createElement('div');
    dropMarker.className = 'kanban-drop-marker';
    dropMarker.setAttribute('aria-hidden', 'true');

    // TRANSITIONS map from server (data-transitions on .kanban). Used to
    // gray out invalid drop targets during a drag and to refuse drops
    // early on the client. Server still validates as the authority.
    var transitions = {};
    try {
      transitions = JSON.parse(board.getAttribute('data-transitions') || '{}');
    } catch (e) {
      transitions = {};
    }
    var ACTIVE_WORK = { 'Queued': 1, 'In Progress': 1, 'Ready': 1 };
    function canDrop(fromStatus, toStatus, cardEl) {
      if (!fromStatus || !toStatus) return true;        // permissive when map missing
      if (fromStatus === toStatus) return true;          // same-column reorder
      var allowed = transitions[fromStatus];
      if (!Array.isArray(allowed) || allowed.indexOf(toStatus) === -1) return false;
      // Backlog → Queued additionally requires an assignee. Active columns
      // assume work has an owner; server enforces this too.
      if (fromStatus === 'Backlog' && toStatus === 'Queued' && cardEl) {
        if (cardEl.getAttribute('data-assigned') !== 'true') return false;
      }
      // Agent-assigned tasks drive their own status across Queued / In
      // Progress / Ready. PMs can still pull them to Backlog or Cancelled —
      // they just can't shuffle them within the active workflow.
      if (cardEl && cardEl.getAttribute('data-assignee-type') === 'agent'
          && ACTIVE_WORK[fromStatus] && ACTIVE_WORK[toStatus]) {
        return false;
      }
      return true;
    }

    function clearDragMarker() {
      if (dropMarker.parentNode) dropMarker.parentNode.removeChild(dropMarker);
    }

    function clearDraggingClass() {
      var els = document.querySelectorAll('.kanban-card.kanban-card--dragging');
      Array.prototype.forEach.call(els, function (el) {
        el.classList.remove('kanban-card--dragging');
      });
    }

    function clearInvalidDropMarks() {
      var cols = board.querySelectorAll('.kanban-col--invalid-drop');
      Array.prototype.forEach.call(cols, function (c) {
        c.classList.remove('kanban-col--invalid-drop');
      });
    }

    // Bubble-phase listener — runs after DragDrop's per-card ondragstart,
    // so multi.selectedIds already reflects the actual dragged set.
    board.addEventListener('dragstart', function (e) {
      var card = e.target.closest('.kanban-card');
      if (!card) return;
      dragSourceCol = card.closest('.kanban-col');
      draggedIds = multi.getSelected().slice();
      if (draggedIds.length === 0) {
        draggedIds = [card.getAttribute('data-task-id')];
      }
      draggedIds.forEach(function (id) {
        var c = document.querySelector('.kanban-card[data-task-id="' + id + '"]');
        if (c) c.classList.add('kanban-card--dragging');
      });
      // Mark invalid drop targets based on TRANSITIONS from the source.
      // For multi-drag, take the most-restrictive: a column is invalid if
      // ANY dragged card couldn't move there.
      var fromStatus = dragSourceCol && dragSourceCol.getAttribute('data-status');
      var draggedCards = draggedIds.map(function (id) {
        return document.querySelector('.kanban-card[data-task-id="' + id + '"]');
      }).filter(Boolean);
      board.querySelectorAll('.kanban-col[data-status]').forEach(function (col) {
        var toStatus = col.getAttribute('data-status');
        var ok = draggedCards.every(function (c) { return canDrop(fromStatus, toStatus, c); });
        if (!ok) col.classList.add('kanban-col--invalid-drop');
      });
    });

    board.addEventListener('dragover', function (e) {
      if (!dragSourceCol) return;
      var col = e.target.closest('.kanban-col');
      if (!col || col !== dragSourceCol) {
        clearDragMarker();
        return;
      }
      var bodyEl = col.querySelector('.kanban-col-body');
      if (!bodyEl) return;

      var draggedSet = {};
      draggedIds.forEach(function (id) { draggedSet[id] = true; });

      var siblings = Array.prototype.slice.call(bodyEl.querySelectorAll('.kanban-card'))
        .filter(function (el) { return !draggedSet[el.getAttribute('data-task-id')]; });

      var insertBefore = null;
      for (var i = 0; i < siblings.length; i++) {
        var rect = siblings[i].getBoundingClientRect();
        var mid = rect.top + rect.height / 2;
        if (e.clientY < mid) { insertBefore = siblings[i]; break; }
      }

      if (insertBefore) {
        if (dropMarker.parentNode !== bodyEl || dropMarker.nextSibling !== insertBefore) {
          bodyEl.insertBefore(dropMarker, insertBefore);
        }
      } else {
        if (dropMarker.parentNode !== bodyEl || dropMarker.nextSibling !== null) {
          bodyEl.appendChild(dropMarker);
        }
      }
    });

    function endDrag() {
      clearDragMarker();
      clearDraggingClass();
      clearInvalidDropMarks();
      dragSourceCol = null;
      draggedIds = [];
    }
    board.addEventListener('dragend', endDrag);
    board.addEventListener('drop', endDrag);

    // Each column is a drop zone. The drop handler distinguishes:
    //   • within-column reorder — all dragged ids already belong to the
    //     target column. Compute the new visual order from drop Y and POST
    //     /tasks/reorder with the full new id list.
    //   • cross-column move — at least one dragged id is from a different
    //     column. POST /tasks/move-batch (or /tasks/:id/move for a single).
    //     Server appends to the bottom of the destination column.
    board.querySelectorAll('.kanban-col[data-status]').forEach(function (col) {
      window.DragDrop.makeDropZone(col, {
        hoverClass: 'kanban-col-hover',
        onDrop: function (taskIds, e) {
          var newStatus = col.getAttribute('data-status');
          var allFromThisCol = taskIds.every(function (id) {
            var card = document.querySelector('.kanban-card[data-task-id="' + id + '"]');
            return card && card.closest('.kanban-col') === col;
          });
          // Cross-column drop — validate transition (and assignment, where
          // required) per-card before the round trip. Within-column reorder
          // is always allowed regardless of TRANSITIONS.
          if (!allFromThisCol) {
            var rejected = taskIds.some(function (id) {
              var card = document.querySelector('.kanban-card[data-task-id="' + id + '"]');
              var srcCol = card && card.closest('.kanban-col');
              var from = srcCol && srcCol.getAttribute('data-status');
              return !canDrop(from, newStatus, card);
            });
            if (rejected) {
              col.classList.add('kanban-col--reject');
              setTimeout(function () { col.classList.remove('kanban-col--reject'); }, 400);
              return;  // refuse the drop entirely
            }
          }
          if (allFromThisCol) {
            var newOrder = computeNewColumnOrder(col, taskIds, e.clientY);
            reorderColumn(newStatus, newOrder, col);
          } else if (taskIds.length > 1) {
            moveTasksBatch(taskIds, newStatus, col);
          } else if (taskIds.length === 1) {
            moveTask(taskIds[0], newStatus, col);
          }
        }
      });
    });

    // Compute the column's new ordered id list given the dragged ids and the
    // drop Y coordinate. The dragged ids are removed from their current
    // position and inserted before the first remaining card whose vertical
    // midpoint is below the drop Y. If the drop is past every card, they're
    // appended at the end.
    function computeNewColumnOrder(col, draggedIds, dropY) {
      var draggedSet = {};
      draggedIds.forEach(function (id) { draggedSet[id] = true; });

      var bodyEl = col.querySelector('.kanban-col-body');
      var siblings = Array.prototype.slice.call(
        bodyEl ? bodyEl.querySelectorAll('.kanban-card') : []
      );
      var remaining = siblings.filter(function (el) {
        return !draggedSet[el.getAttribute('data-task-id')];
      });

      // Use the cards' current visual (DOM) order for the inserted block
      // rather than the order ids were selected, so multi-drag preserves
      // the cluster's existing layout.
      var draggedInOrder = siblings
        .filter(function (el) { return draggedSet[el.getAttribute('data-task-id')]; })
        .map(function (el) { return el.getAttribute('data-task-id'); });

      var insertIdx = remaining.length;
      for (var i = 0; i < remaining.length; i++) {
        var rect = remaining[i].getBoundingClientRect();
        var mid = rect.top + rect.height / 2;
        if (dropY < mid) { insertIdx = i; break; }
      }

      var out = [];
      for (var j = 0; j < remaining.length; j++) {
        if (j === insertIdx) {
          draggedInOrder.forEach(function (id) { out.push(id); });
        }
        out.push(remaining[j].getAttribute('data-task-id'));
      }
      if (insertIdx === remaining.length) {
        draggedInOrder.forEach(function (id) { out.push(id); });
      }
      return out;
    }

    // Apply a new ordered id list to the DOM in the column body, then POST
    // to /tasks/reorder so the server persists priorities.
    function reorderColumn(newStatus, orderedIds, targetCol) {
      var bodyEl = targetCol.querySelector('.kanban-col-body');
      if (!bodyEl) return;
      var empty = bodyEl.querySelector('.kanban-empty');
      if (empty) empty.parentNode.removeChild(empty);

      // Reorder DOM optimistically.
      orderedIds.forEach(function (id) {
        var card = document.querySelector('.kanban-card[data-task-id="' + id + '"]');
        if (card) bodyEl.appendChild(card);
      });

      var url = '/org/' + orgSlug + '/projects/' + projectId + '/tasks/reorder';
      var body = new URLSearchParams();
      body.set('_csrf', csrf);
      body.set('status', newStatus);
      body.set('ids', orderedIds.join(','));

      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
        .then(function (res) {
          if (!res.ok) throw new Error('reorder failed: ' + res.status);
          return res.json();
        })
        .then(function () {
          updateCounts();
          refreshSelectionUi();
        })
        .catch(function (err) {
          console.error(err);
          alert('Could not reorder tasks: ' + err.message);
        });
    }

    function moveCardDom(taskId, targetCol) {
      var card = document.querySelector('.kanban-card[data-task-id="' + taskId + '"]');
      if (!card) return;
      var body = targetCol.querySelector('.kanban-col-body');
      if (!body) return;
      var empty = body.querySelector('.kanban-empty');
      if (empty) empty.parentNode.removeChild(empty);
      body.appendChild(card);
      // Keep the card's data-status in sync with its column so CSS rules
      // that gate action buttons (Reject/Complete on Ready, Activate on
      // Backlog) follow the move.
      var newStatus = targetCol.getAttribute('data-status');
      if (newStatus) card.setAttribute('data-status', newStatus);
      syncReadyButtons(card);
    }

    // Ensure Reject + Complete buttons are present on Ready cards (PM
    // only, not when awaiting input) and absent everywhere else. Called
    // from every move path so cards that arrive in Ready via local drag
    // or remote WS get their action buttons injected, and cards that
    // leave Ready have them removed.
    var isPM = board.getAttribute('data-is-pm') === 'true';
    function syncReadyButtons(card) {
      var status = card.getAttribute('data-status');
      var awaitingInput = card.classList.contains('kanban-card--alert');
      var shouldHave = isPM && status === 'Ready' && !awaitingInput;
      var existingReject   = card.querySelector('.kanban-card__reject');
      var existingComplete = card.querySelector('.kanban-card__complete');
      if (shouldHave) {
        var taskId = card.getAttribute('data-task-id');
        if (!existingReject) {
          var rj = document.createElement('button');
          rj.type = 'button';
          rj.className = 'kanban-card__reject';
          rj.setAttribute('data-task-id', taskId);
          rj.title = 'Reject (send back to Queued)';
          rj.setAttribute('aria-label', 'Reject task');
          rj.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 14l-4-4 4-4"/><path d="M5 10h9a5 5 0 0 1 0 10h-2"/></svg>';
          card.appendChild(rj);
        }
        if (!existingComplete) {
          var cm = document.createElement('button');
          cm.type = 'button';
          cm.className = 'kanban-card__complete';
          cm.setAttribute('data-task-id', taskId);
          cm.title = 'Complete (accept work)';
          cm.setAttribute('aria-label', 'Complete task');
          cm.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7"/></svg>';
          card.appendChild(cm);
        }
      } else {
        if (existingReject && existingReject.parentNode) {
          existingReject.parentNode.removeChild(existingReject);
        }
        if (existingComplete && existingComplete.parentNode) {
          existingComplete.parentNode.removeChild(existingComplete);
        }
      }
    }

    function moveTask(taskId, newStatus, targetCol) {
      var url = '/org/' + orgSlug + '/projects/' + projectId + '/tasks/' + taskId + '/move';
      var body = new URLSearchParams();
      body.set('_csrf', csrf);
      body.set('status', newStatus);

      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
        .then(function (res) {
          if (!res.ok) throw new Error('move failed: ' + res.status);
          return res.json();
        })
        .then(function () {
          moveCardDom(taskId, targetCol);
          updateCounts();
        })
        .catch(function (err) {
          console.error(err);
          alert('Could not move task: ' + err.message);
        });
    }

    function moveTasksBatch(taskIds, newStatus, targetCol) {
      var url = '/org/' + orgSlug + '/projects/' + projectId + '/tasks/move-batch';
      var body = new URLSearchParams();
      body.set('_csrf', csrf);
      body.set('status', newStatus);
      body.set('ids', taskIds.join(','));

      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
        .then(function (res) {
          if (!res.ok) throw new Error('batch move failed: ' + res.status);
          return res.json();
        })
        .then(function () {
          taskIds.forEach(function (id) { moveCardDom(id, targetCol); });
          updateCounts();
          // Selection persists post-move so the user can keep working on the
          // same group; clear if they prefer via the bar's Clear button or Esc.
          refreshSelectionUi();
        })
        .catch(function (err) {
          console.error(err);
          alert('Could not move tasks: ' + err.message);
        });
    }

    function updateCounts() {
      board.querySelectorAll('.kanban-col').forEach(function (col) {
        var n = col.querySelectorAll('.kanban-card').length;
        var c = col.querySelector('.kanban-count');
        if (c) c.textContent = n;
      });
    }

    // ── Backlog card inline assign + activate ───────────────────────────
    // The assignee dropdown POSTs to /assign and updates the card's
    // data-assigned attribute + activate-button visibility in place. The
    // activate arrow POSTs to /activate; on success the WS broadcast
    // shows the card moving to Queued, no extra DOM work needed here.

    // Adds or removes the activate button from a card based on assignment
    // state. The button isn't rendered server-side when no assignee — so
    // we have to create it client-side when one is added, and tear it
    // down when removed.
    function setCardAssigned(card, isAssigned) {
      card.setAttribute('data-assigned', isAssigned ? 'true' : 'false');
      var row = card.querySelector('.kanban-card__bottom-row');
      var existing = card.querySelector('.kanban-card__activate');
      if (isAssigned && !existing && row) {
        var taskId = card.getAttribute('data-task-id');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kanban-card__activate';
        btn.setAttribute('data-task-id', taskId);
        btn.title = 'Activate (move to Queued)';
        btn.setAttribute('aria-label', 'Activate task');
        btn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';
        row.appendChild(btn);
      } else if (!isAssigned && existing) {
        existing.parentNode.removeChild(existing);
      }
    }

    board.addEventListener('change', function (e) {
      var sel = e.target.closest('.kanban-card__assign-select');
      if (!sel) return;
      var taskId = sel.getAttribute('data-task-id');
      var assignee = sel.value || '';
      var card = sel.closest('.kanban-card');
      sel.disabled = true;
      var url = '/org/' + orgSlug + '/projects/' + projectId + '/tasks/' + taskId + '/assign';
      var body = new URLSearchParams();
      body.set('_csrf', csrf);
      body.set('assignee', assignee);
      fetch(url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (j) { throw new Error(j.error || 'assign failed: ' + res.status); });
          return res.json();
        })
        .then(function () {
          setCardAssigned(card, !!assignee);
        })
        .catch(function (err) {
          console.error(err);
          alert('Could not assign task: ' + err.message);
          // Revert the dropdown selection to whatever the card currently shows.
          sel.value = card.getAttribute('data-assigned') === 'true'
            ? (sel.querySelector('option[selected]') || {}).value || ''
            : '';
        })
        .finally(function () { sel.disabled = false; });
    });

    board.addEventListener('click', function (e) {
      var btn = e.target.closest('.kanban-card__activate');
      if (!btn) return;
      e.preventDefault();
      var taskId = btn.getAttribute('data-task-id');
      btn.disabled = true;
      var url = '/org/' + orgSlug + '/projects/' + projectId + '/tasks/' + taskId + '/activate';
      var body = new URLSearchParams();
      body.set('_csrf', csrf);
      fetch(url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        body: body.toString()
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (j) { throw new Error(j.error || 'activate failed: ' + res.status); });
          // The WS broadcast will move the card visually; no DOM work here.
        })
        .catch(function (err) {
          console.error(err);
          alert('Could not activate task: ' + err.message);
        })
        .finally(function () { btn.disabled = false; });
    });

    // Reject button on Ready cards — the human rejection moment. Prompts
    // for a reason (required), POSTs to /reject which logs the reason as
    // a comment + bounces the task back to Queued. WS broadcast moves
    // the card visually.
    board.addEventListener('click', function (e) {
      var btn = e.target.closest('.kanban-card__reject');
      if (!btn) return;
      e.preventDefault();
      var reason = (window.prompt('Why is this task being rejected? The assignee will see your reason on the task.') || '').trim();
      if (!reason) return;        // user cancelled or left it empty
      var taskId = btn.getAttribute('data-task-id');
      btn.disabled = true;
      var url = '/org/' + orgSlug + '/projects/' + projectId + '/tasks/' + taskId + '/reject';
      var body = new URLSearchParams();
      body.set('_csrf', csrf);
      body.set('reason', reason);
      fetch(url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (j) { throw new Error(j.error || 'reject failed: ' + res.status); });
          // WS broadcast handles the visual Ready → Queued move.
        })
        .catch(function (err) {
          console.error(err);
          alert('Could not reject task: ' + err.message);
        })
        .finally(function () { btn.disabled = false; });
    });

    // Retry button on intervention-flagged cards. POSTs to /retry which
    // clears the flag, resets activity, and moves the task to Queued.
    // The WS 'moved' broadcast handles the visual transition; we don't
    // navigate away from the board.
    board.addEventListener('click', function (e) {
      var btn = e.target.closest('.kanban-card__retry');
      if (!btn) return;
      e.preventDefault();
      var taskId = btn.getAttribute('data-task-id');
      btn.disabled = true;
      var url = '/org/' + orgSlug + '/projects/' + projectId + '/tasks/' + taskId + '/retry';
      var body = new URLSearchParams();
      body.set('_csrf', csrf);
      fetch(url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (j) { throw new Error(j.error || 'retry failed: ' + res.status); });
          // WS broadcast moves the card to Queued; we don't reload.
        })
        .catch(function (err) {
          console.error(err);
          alert('Could not retry task: ' + err.message);
        })
        .finally(function () { btn.disabled = false; });
    });

    // Cancel button on intervention-flagged cards. Sibling to Retry —
    // gives the PM a one-click "this work is no longer needed" escape.
    // POSTs to /cancel which moves the task to Cancelled (terminal),
    // removing it from the active board.
    board.addEventListener('click', function (e) {
      var btn = e.target.closest('.kanban-card__cancel');
      if (!btn) return;
      e.preventDefault();
      if (!window.confirm('Cancel this task? It will be marked as Cancelled and drop off the active board.')) return;
      var taskId = btn.getAttribute('data-task-id');
      btn.disabled = true;
      var url = '/org/' + orgSlug + '/projects/' + projectId + '/tasks/' + taskId + '/cancel';
      var body = new URLSearchParams();
      body.set('_csrf', csrf);
      fetch(url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
        .then(function (res) {
          // task.cancel returns a redirect on success; the fetch follows
          // the 302 and we get back the task-detail HTML. Accept either
          // a 2xx or a 3xx-followed redirect — both mean success.
          if (!res.ok && res.status !== 0) {
            // Best-effort error parse — fall back to status text if not JSON.
            return res.text().then(function (t) {
              throw new Error(t && t.length < 200 ? t : 'cancel failed: ' + res.status);
            });
          }
          // WS 'moved' broadcast removes the card from the board.
        })
        .catch(function (err) {
          console.error(err);
          alert('Could not cancel task: ' + err.message);
        })
        .finally(function () { btn.disabled = false; });
    });

    // Complete button on Ready cards — the human acceptance moment.
    // POSTs to /move with status=Completed. WS broadcast removes the card
    // from the Ready column (Completed lives in All Tasks, not the board).
    board.addEventListener('click', function (e) {
      var btn = e.target.closest('.kanban-card__complete');
      if (!btn) return;
      e.preventDefault();
      var taskId = btn.getAttribute('data-task-id');
      btn.disabled = true;
      var card = btn.closest('.kanban-card');
      var url = '/org/' + orgSlug + '/projects/' + projectId + '/tasks/' + taskId + '/move';
      var body = new URLSearchParams();
      body.set('_csrf', csrf);
      body.set('status', 'Completed');
      fetch(url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (j) { throw new Error(j.error || 'complete failed: ' + res.status); });
          // Completed is not a board column — pull the card out of the DOM.
          if (card && card.parentNode) card.parentNode.removeChild(card);
          updateCounts();
        })
        .catch(function (err) {
          console.error(err);
          alert('Could not complete task: ' + err.message);
        })
        .finally(function () { btn.disabled = false; });
    });

    // ── Per-card move menu (mobile-first) ────────────────────────────
    // Drag-and-drop doesn't behave on touch devices (competes with the
    // horizontal column scroll). The move menu is a kebab on each card
    // that opens a popover listing valid status transitions for this
    // card's current status. CSS hides the trigger above the touch
    // breakpoint; desktop continues to use drag-and-drop.
    //
    // Filters honor canDrop() so the agent-task restriction (PMs can
    // only pull agent tasks to Backlog or Cancelled, not shuffle them
    // mid-workflow) is consistent with drag behavior.
    var moveMenuPopover = null;
    function closeMoveMenu() {
      if (moveMenuPopover && moveMenuPopover.parentNode) {
        moveMenuPopover.parentNode.removeChild(moveMenuPopover);
      }
      moveMenuPopover = null;
      var openTriggers = board.querySelectorAll('.kanban-card__move-menu[aria-expanded="true"]');
      Array.prototype.forEach.call(openTriggers, function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
    }
    board.addEventListener('click', function (e) {
      var trigger = e.target.closest('.kanban-card__move-menu');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();

      // Toggle: if this trigger is the one currently open, close.
      if (trigger.getAttribute('aria-expanded') === 'true') {
        closeMoveMenu();
        return;
      }
      closeMoveMenu();

      var card = trigger.closest('.kanban-card');
      if (!card) return;
      var fromStatus = card.getAttribute('data-status');
      var taskId = card.getAttribute('data-task-id');

      // Build the destination list: every status reachable from
      // fromStatus per TRANSITIONS that also satisfies canDrop()
      // (handles agent-task restrictions).
      var dests = Array.isArray(transitions[fromStatus]) ? transitions[fromStatus] : [];
      var valid = dests.filter(function (toStatus) { return canDrop(fromStatus, toStatus, card); });
      if (valid.length === 0) return;  // nothing to move to

      // Render the popover. Position relative to the trigger.
      moveMenuPopover = document.createElement('div');
      moveMenuPopover.className = 'kanban-card__move-popover';
      moveMenuPopover.setAttribute('role', 'menu');
      valid.forEach(function (toStatus) {
        var item = document.createElement('button');
        item.type = 'button';
        item.className = 'kanban-card__move-item';
        item.setAttribute('role', 'menuitem');
        item.textContent = 'Move to ' + toStatus;
        item.addEventListener('click', function (ev) {
          ev.preventDefault();
          ev.stopPropagation();
          closeMoveMenu();
          // Find the destination column. If Cancelled (not on the
          // active board), use a sentinel: optimistic remove + POST.
          var destCol = board.querySelector('.kanban-col[data-status="' + toStatus + '"]');
          if (destCol) {
            moveTask(taskId, toStatus, destCol);
          } else {
            // Off-board destination (e.g. Cancelled). POST then drop
            // the card from the DOM on success — the WS broadcast
            // would also remove it, but doing it here keeps the UI
            // snappy on slow networks.
            var url = '/org/' + orgSlug + '/projects/' + projectId + '/tasks/' + taskId + '/move';
            var body = new URLSearchParams();
            body.set('_csrf', csrf);
            body.set('status', toStatus);
            fetch(url, {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: body.toString()
            })
              .then(function (res) {
                if (!res.ok) return res.json().then(function (j) { throw new Error(j.error || 'move failed: ' + res.status); });
                if (card && card.parentNode) card.parentNode.removeChild(card);
                updateCounts();
              })
              .catch(function (err) {
                console.error(err);
                alert('Could not move task: ' + err.message);
              });
          }
        });
        moveMenuPopover.appendChild(item);
      });
      card.appendChild(moveMenuPopover);
      trigger.setAttribute('aria-expanded', 'true');
    });
    // Click anywhere outside the popover closes it.
    document.addEventListener('click', function (e) {
      if (!moveMenuPopover) return;
      if (e.target.closest('.kanban-card__move-popover')) return;
      if (e.target.closest('.kanban-card__move-menu')) return;
      closeMoveMenu();
    });
    // Escape closes it.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && moveMenuPopover) closeMoveMenu();
    });

    function flashCard(card) {
      if (!card) return;
      card.classList.remove('kanban-card--remote-update');
      // Force reflow so re-adding the class restarts the animation.
      void card.offsetWidth;
      card.classList.add('kanban-card--remote-update');
      setTimeout(function () {
        card.classList.remove('kanban-card--remote-update');
      }, 900);
    }

    // Activity-line management. The agent runtime POSTs to
    // /api/agent/tasks/:id/activity per LLM iter and the portal broadcasts
    // a WS task:event with type='activity'. We inject the line on first
    // arrival, overwrite text on subsequent updates, and mark it stale
    // after 5 minutes of silence.
    var STALE_AFTER_MS = 5 * 60 * 1000;
    function setCardActivity(card, note, atIso) {
      if (!card) return;
      var line = card.querySelector('.kanban-card__activity');
      if (!note) {
        if (line && line.parentNode) line.parentNode.removeChild(line);
        return;
      }
      if (!line) {
        line = document.createElement('p');
        line.className = 'kanban-card__activity';
        line.innerHTML = '<span class="kanban-card__activity-icon">▸</span>' +
                         '<span class="kanban-card__activity-text"></span>';
        // Insert after the description (or right after the title link).
        var desc = card.querySelector('.kanban-desc');
        var afterEl = desc || card.querySelector('h4');
        if (afterEl && afterEl.nextSibling) {
          afterEl.parentNode.insertBefore(line, afterEl.nextSibling);
        } else if (afterEl) {
          afterEl.parentNode.appendChild(line);
        } else {
          card.appendChild(line);
        }
      }
      var textEl = line.querySelector('.kanban-card__activity-text');
      if (textEl) textEl.textContent = note;
      if (atIso) line.setAttribute('data-activity-at', atIso);
      line.classList.remove('kanban-card__activity--stale');
    }

    // Every 30s sweep all activity lines and flag the stale ones. Cheap;
    // a board has at most a few dozen cards.
    setInterval(function () {
      var now = Date.now();
      board.querySelectorAll('.kanban-card__activity[data-activity-at]').forEach(function (line) {
        var at = Date.parse(line.getAttribute('data-activity-at'));
        if (!Number.isFinite(at)) return;
        if (now - at > STALE_AFTER_MS) {
          line.classList.add('kanban-card__activity--stale');
        } else {
          line.classList.remove('kanban-card__activity--stale');
        }
      });
    }, 30000);

    // React to task events broadcast over Socket.IO from this or other clients.
    // The local actor already updated optimistically; idempotent ops are safe.
    window.addEventListener('rushworks:task-event', function (e) {
      var p = e.detail;
      if (!p) return;
      if (p.type === 'activity') {
        var aCard = document.querySelector('.kanban-card[data-task-id="' + p.taskId + '"]');
        if (aCard) setCardActivity(aCard, p.note, p.at);
        return;
      }
      if (p.type === 'moved') {
        var card = document.querySelector('.kanban-card[data-task-id="' + p.taskId + '"]');
        var col = document.querySelector('.kanban-col[data-status="' + p.to + '"]');
        if (!card || !col) return;
        // Always update data-status — even if the card is already in the
        // right column (e.g. echo of our own optimistic move).
        if (p.to) card.setAttribute('data-status', p.to);
        syncReadyButtons(card);
        // Clear any stale activity line on column change. The agent will
        // emit a fresh one if it picks the task back up.
        setCardActivity(card, null);
        var dest = col.querySelector('.kanban-col-body');
        if (!dest || card.parentNode === dest) return;
        var empty = dest.querySelector('.kanban-empty');
        if (empty) empty.parentNode.removeChild(empty);
        dest.appendChild(card);
        updateCounts();
        flashCard(card);
      } else if (p.type === 'deleted') {
        var dCard = document.querySelector('.kanban-card[data-task-id="' + p.taskId + '"]');
        if (dCard) {
          flashCard(dCard);
          setTimeout(function () {
            if (dCard.parentNode) dCard.parentNode.removeChild(dCard);
            updateCounts();
          }, 600);
        }
      } else if (p.type === 'created' || p.type === 'updated') {
        // We don't have the rendered card locally; reload to pull fresh data.
        location.reload();
      }
    });
  }

  function initTaskDetail() {
    var marker = document.querySelector('[data-realtime-task-id]');
    if (!marker) return;
    var taskId = parseInt(marker.getAttribute('data-realtime-task-id'), 10);
    if (!Number.isInteger(taskId) || taskId <= 0) return;

    // Track the latest event we processed locally so the broadcast echo of
    // our own action doesn't trigger a redundant reload.
    var lastSelfEventAt = 0;
    document.addEventListener('submit', function () {
      lastSelfEventAt = Date.now();
    }, true);

    window.addEventListener('rushworks:task-event', function (e) {
      var p = e.detail;
      if (!p || p.taskId !== taskId) return;
      // Suppress reload if we just submitted a form (likely the source of this event).
      if (Date.now() - lastSelfEventAt < 1500) return;
      // Activity events fire continuously during agent work — they only
      // affect the kanban card's live progress line, which isn't shown on
      // the task detail page. Ignore them entirely here.
      if (p.type === 'activity') return;
      // If the user is mid-edit (form field focused), don't yank the page
      // out from under them. They'd lose typed input on reload. The next
      // event after they blur will catch them up, and a manual refresh
      // is always available.
      var active = document.activeElement;
      if (active && (
        active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.tagName === 'SELECT' ||
        active.isContentEditable
      )) return;
      if (p.type === 'deleted') {
        // Task is gone — bounce to the project tasks board.
        var board = document.querySelector('a[href*="/tasks"][href*="/projects/"]');
        if (board) {
          window.location.href = board.getAttribute('href');
        } else {
          location.reload();
        }
      } else {
        location.reload();
      }
    });
  }

  function init() {
    initKanban();
    initTaskDetail();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  console.log('RushworksAI client loaded');
})();
